import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axiosInstance from "../api/axiosConfig";
import storage from "../utils/storage";
import { useAuth } from "./AuthContext";

// Units of measure live on the server (GET/POST/PUT/DELETE /units). The
// server _id is the id; `id` is an alias added by the API.

const LEGACY_KEY = "units";

const errMsg = (err, fallback) => err?.error || err?.message || fallback;

// Normalise a server record for consumers.
const fromServer = (u) => ({
  ...u,
  id: u.id || u._id,
  baseUnitId: u.baseUnitId ? String(u.baseUnitId) : "",
});

const toServer = (u) => {
  const payload = {
    name: (u.name || "").trim(),
    symbol: (u.symbol || "").trim(),
    isBase: u.isBase !== false,
  };
  if (u.category !== undefined) payload.category = u.category;
  if (!payload.isBase) {
    payload.baseUnitId = u.baseUnitId;
    payload.conversionFactor = Number(u.conversionFactor);
  }
  return payload;
};

const UnitOfMeasureContext = createContext();

export const useUnitOfMeasure = () => useContext(UnitOfMeasureContext);

export const UnitOfMeasureProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?._id || user?.id || null;
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedFor = useRef(null);

  const fetchUnits = useCallback(async () => {
    const res = await axiosInstance.get("/units", { params: { limit: 500 } });
    return (res.data?.data || []).map(fromServer);
  }, []);

  // One-time upload of units kept in localStorage by the old implementation.
  const migrateLegacy = useCallback(async (serverUnits) => {
    const legacy = storage.getJSON(LEGACY_KEY);
    if (!Array.isArray(legacy) || legacy.length === 0) {
      storage.removeItem(LEGACY_KEY);
      return serverUnits;
    }
    const byName = new Map(serverUnits.map((u) => [u.name.toLowerCase(), u]));
    const idMap = new Map(); // legacy id -> server id
    legacy.forEach((l) => {
      const hit = byName.get(String(l.name || "").toLowerCase());
      if (hit) idMap.set(l.id, hit.id);
    });
    let failed = false;
    const upload = async (l) => {
      if (!l.name || !l.symbol || byName.has(l.name.toLowerCase())) return;
      try {
        const res = await axiosInstance.post(
          "/units",
          toServer({
            ...l,
            baseUnitId: l.isBase ? "" : idMap.get(l.baseUnitId),
          })
        );
        const saved = fromServer(res.data.data);
        byName.set(saved.name.toLowerCase(), saved);
        idMap.set(l.id, saved.id);
      } catch (e) {
        failed = true;
      }
    };
    for (const l of legacy.filter((x) => x.isBase)) await upload(l);
    for (const l of legacy.filter((x) => !x.isBase)) {
      if (idMap.get(l.baseUnitId)) await upload(l);
    }
    if (!failed) storage.removeItem(LEGACY_KEY);
    return Array.from(byName.values());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let list = await fetchUnits();
      if (list.length === 0) {
        try {
          await axiosInstance.post("/units/seed-defaults");
          list = await fetchUnits();
        } catch (e) {
          // Non-admins cannot seed; fall through with whatever the org has.
        }
      }
      const merged = await migrateLegacy(list);
      if (merged.length !== list.length) list = await fetchUnits();
      setUnits(list);
    } catch (err) {
      setError(errMsg(err, "Failed to load units of measure"));
    } finally {
      setLoading(false);
    }
  }, [fetchUnits, migrateLegacy]);

  useEffect(() => {
    if (!userId) {
      loadedFor.current = null;
      setUnits([]);
      return;
    }
    if (loadedFor.current === userId) return;
    loadedFor.current = userId;
    load();
  }, [userId, load]);

  // All mutators are async and return the saved unit (add/update) or
  // { success, message } (delete). They throw on add/update failure.
  const addUnit = async (unit) => {
    try {
      const res = await axiosInstance.post("/units", toServer(unit));
      const saved = fromServer(res.data.data);
      setUnits((prev) => [...prev, saved]);
      return saved;
    } catch (err) {
      throw new Error(errMsg(err, "Failed to save unit"));
    }
  };

  const updateUnit = async (id, updatedUnit) => {
    try {
      const res = await axiosInstance.put(`/units/${id}`, toServer(updatedUnit));
      const saved = fromServer(res.data.data);
      setUnits((prev) => prev.map((u) => (u.id === id ? saved : u)));
      return saved;
    } catch (err) {
      throw new Error(errMsg(err, "Failed to update unit"));
    }
  };

  const deleteUnit = async (id) => {
    try {
      await axiosInstance.delete(`/units/${id}`);
      setUnits((prev) => prev.filter((u) => u.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, message: errMsg(err, "Failed to delete unit") };
    }
  };

  const getUnit = useCallback((id) => units.find((u) => u.id === id), [units]);
  const getBaseUnits = useCallback(() => units.filter((u) => u.isBase), [units]);

  // Convert quantity from one unit to another
  const convertUnit = (fromUnitId, toUnitId, quantity) => {
    const fromUnit = getUnit(fromUnitId);
    const toUnit = getUnit(toUnitId);
    if (!fromUnit || !toUnit) return null;
    if (fromUnit.isBase && toUnit.isBase) {
      return fromUnitId === toUnitId ? quantity : null;
    }
    if (!fromUnit.isBase && fromUnit.baseUnitId === toUnitId) {
      return quantity * fromUnit.conversionFactor;
    }
    if (!toUnit.isBase && toUnit.baseUnitId === fromUnitId) {
      return quantity / toUnit.conversionFactor;
    }
    return null;
  };

  return (
    <UnitOfMeasureContext.Provider
      value={{
        units,
        loading,
        error,
        reload: load,
        addUnit,
        updateUnit,
        deleteUnit,
        getUnit,
        getBaseUnits,
        convertUnit,
      }}
    >
      {children}
    </UnitOfMeasureContext.Provider>
  );
};

export default UnitOfMeasureContext;
